import React from 'react';
import { Tooltip, type TooltipPlacement } from './ui/Tooltip';

export interface TroubleTicketEntry {
  recno: number;
  datetime: string;
  category: string;
  status: string;
}

interface TroubleTicketBadgeProps {
  /** Tickets to list in the hover tooltip. */
  tickets: TroubleTicketEntry[];
  /** Total ticket count shown in the badge. */
  count: number;
  /** Dark-mode flag — tooltip is portaled, so colors are applied inline. */
  /** Click handler (e.g. open the order drawer/modal). */
  onClick?: () => void;
  /** Tooltip placement; defaults to 'right'. */
  placement?: TooltipPlacement;
}

/**
 * Small count badge for an order's trouble tickets with a hover tooltip
 * listing each ticket (category, status, datetime). The badge is red when any
 * ticket is NEW/PENDING and green otherwise. Shared by the At-Risk Monitor,
 * Scheduler, and Logistics Coordinator tables. Renders nothing when count < 1.
 */
export const TroubleTicketBadge: React.FC<TroubleTicketBadgeProps> = ({
  tickets,
  count,
  onClick,
  placement = 'right',
}) => {
  if (!count || count < 1) {
    return null;
  }

  const hasActive = tickets.some(
    (t) => t.status.toUpperCase() === 'NEW' || t.status.toUpperCase() === 'PENDING'
  );

  const tooltipContent = (
    <div className="text-xs space-y-2 max-w-xs">
      <div className="font-semibold pb-1 mb-1 border-b border-gray-300 text-gray-900 dark:border-gray-600 dark:text-gray-50">
        Trouble Tickets ({count})
      </div>
      {tickets.map((ticket) => {
        const status = ticket.status.toUpperCase();
        // Classes, not an inline style keyed off isDark: the dark: variant does
        // the same job without the component needing to know the mode, and a
        // class follows a theme through the ramp while an inline hex cannot.
        const badge = (status === 'NEW' || status === 'PENDING')
          ? 'bg-red-100 text-red-800 dark:bg-red-600/25 dark:text-red-300'
          : status === 'RESOLVED'
            ? 'bg-green-100 text-green-800 dark:bg-green-600/25 dark:text-green-300'
            : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200';
        return (
          <div key={ticket.recno} className="space-y-0.5">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-gray-900 dark:text-gray-50">{ticket.category || 'No Category'}</span>
              <span className={`inline-flex px-1 py-0.5 rounded text-[0.625rem] font-medium ${badge}`}>
                {ticket.status}
              </span>
            </div>
            <div className="text-[0.625rem]" style={{ color: 'rgb(var(--c-gray-400))' }}>{ticket.datetime}</div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Tooltip content={tooltipContent} placement={placement}>
      <span
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded cursor-pointer ${
          hasActive ? 'bg-red-500' : 'bg-green-500'
        }`}
      >
        {count}
      </span>
    </Tooltip>
  );
};
